const MAX_MB = 20;
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/bmp', 'image/tiff'];
const MIN_DIM = 256;

export function validateImageFile(file) {
  if (!file) return 'No file selected';
  if (!ALLOWED_TYPES.includes(file.type) && !file.type.startsWith('image/')) {
    return 'Please upload a valid image file';
  }
  if (file.size > MAX_MB * 1024 * 1024) {
    return `File must be under ${MAX_MB}MB`;
  }
  return null;
}

export function readImageAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read image file'));
    reader.readAsDataURL(file);
  });
}

export function validateImageDimensions(dataUrl, minDim = MIN_DIM) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      if (img.width < minDim || img.height < minDim) {
        reject(new Error(`Image must be at least ${minDim}×${minDim} pixels`));
        return;
      }
      resolve({ width: img.width, height: img.height });
    };
    img.onerror = () => reject(new Error('Could not load image'));
    img.src = dataUrl;
  });
}

export async function processImageFile(file) {
  const fileError = validateImageFile(file);
  if (fileError) throw new Error(fileError);

  const dataUrl = await readImageAsDataUrl(file);
  await validateImageDimensions(dataUrl);
  return dataUrl;
}

/**
 * Decode any browser-supported image and re-encode as JPEG (handles large phone
 * photos / webp / png). Rejects formats the browser cannot draw (e.g. HEIC on many desktops).
 */
export function fileToJpegDataUrl(file, { maxSide = 1600, quality = 0.9 } = {}) {
  return new Promise((resolve, reject) => {
    const fileError = validateImageFile(file);
    if (fileError) {
      reject(new Error(fileError));
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read image file'));
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        if (img.width < MIN_DIM || img.height < MIN_DIM) {
          reject(new Error(`Image must be at least ${MIN_DIM}×${MIN_DIM} pixels`));
          return;
        }
        const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = () =>
        reject(
          new Error(
            'Could not open this image. Use JPG/PNG/WebP, or Capture from the camera instead.'
          )
        );
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}
